import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import { useTranslation } from "react-i18next";
import React from "react";
import { Text } from "src/components/base/text";
import { Subheading } from "src/components/base/heading";
import { Badge, BadgeButton } from "src/components/base/badge";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import { DividerText } from "src/components/base/divider";
import SearchDomainForm from "src/components/search-domain-form";

/**
 * Overview of the DNS search domains: drag & drop to reorder, an inline input at the end of
 * the list to append a new domain.
 *
 * @returns the rendered route component
 */
export default function SearchDomainsOverview() {
    const [t] = useTranslation("admin-dns-search-domains");

    const router = useRouter();
    const [{ list }, { dns_base_domain }] = Route.useLoaderData();

    return (
        <div className={"flex max-w-2xl flex-col gap-5"}>
            <div className={"flex flex-col gap-1"}>
                <Subheading>{t("heading.search-domains")}</Subheading>
                <Text>{t("description.search-domains")}</Text>
                <BadgeButton href={"/a/tenants"}>{t("label.tenant-specific-domains")}</BadgeButton>
            </div>

            <div className={"flex max-w-2xl flex-col gap-2"}>
                <div
                    className={
                        "flex items-center justify-between gap-3 rounded-md bg-(--surface-muted)/40 p-3 ring-1 ring-zinc-950/5 dark:ring-white/10"
                    }
                >
                    <span className={"truncate text-sm/5 font-semibold text-zinc-600 dark:text-zinc-400"}>
                        {dns_base_domain}
                    </span>
                    <Badge title={t("description.base-domain")} className={"cursor-default"}>
                        <LockClosedIcon className={"size-3"} />
                        {t("label.base-domain")}
                    </Badge>
                </div>

                <DividerText soft={true} text={t("divider.reorderable")} className={"my-1"} />

                <SearchDomainForm
                    domains={list}
                    onChange={async () => {
                        await router.invalidate({ sync: true });
                    }}
                />
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/dns/_dns/search-domains")({
    component: SearchDomainsOverview,
    loader: async () => await Promise.all([ApiInternal.dns.getSearchDomains(), ApiInternal.settings.getGeneral()]),
});
